import React, { useMemo } from 'react';

import { Provider as ReduxProvider } from 'react-redux';

import {
  useBi,
  useEnvironment,
  usePanorama,
  useTranslation,
} from '@wix/yoshi-flow-editor';

import { getStore, RootState } from '@/store';

import { useAppExperiments } from 'AiAssistantWidget/experiments';

import { useWidgetServices } from './WidgetServices';
import { useHttpClient } from './HttpClientProvider';

const isDev = process.env.NODE_ENV === 'development';

export const WidgetStoreProvider: React.FC<
  React.PropsWithChildren<{
    initialState?: Partial<RootState>;
  }>
> = React.memo(({ initialState, children }) => {
  const httpClient = useHttpClient();
  const panorama = usePanorama();
  const { t } = useTranslation();
  const bi = useBi();
  const { experiments } = useAppExperiments();
  const environment = useEnvironment();
  const { duplexerConnection, siteEcomService: siteEcom } = useWidgetServices();
  const store = useMemo(
    () =>
      getStore({
        httpClient,
        panorama,
        t,
        bi,
        duplexerConnection,
        siteEcom,
        experiments,
        environment,
        preloadedState: { ...initialState },
        needDevTools: isDev,
        needLogger: isDev,
      }),

    /**
     * We never recreate the storage unless it's a new page load.
     *
     * TODO: maybe HTTP client should be provided as reference so
     * we are no re-creating store when it is updated
     * (and it is updated on instance change)
     *
     * How about duplexerConnection?
     * As there we need to re-create subscriptions.
     *
     * How about other deps, like siteEcom?
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [httpClient, duplexerConnection],
  );

  return <ReduxProvider store={store}>{children}</ReduxProvider>;
});
